// Reportes de nómina: rol general consolidado, provisiones mensuales y costo
// laboral por departamento. Nómina → Reportes.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  MESES, fieldCls, money, nomRpc,
  type RepCostoRow, type RepProvisionRow, type RepRolGeneralData, TipoRolSelect,
} from './shared';

const HOY = new Date();

function SelectorPeriodo({ anio, mes, setAnio, setMes, cargando, tipo, setTipo }: {
  anio: number; mes: number; setAnio: (n: number) => void; setMes: (n: number) => void; cargando: boolean;
  tipo?: string; setTipo?: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
        {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
      {setTipo && <TipoRolSelect value={tipo ?? ''} onChange={setTipo} />}
      {cargando && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
    </div>
  );
}

function SinRol({ anio, mes }: { anio: number; mes: number }) {
  return (
    <Card><CardContent className="py-10 text-center text-muted-foreground">
      No hay rol generado para {MESES[mes - 1]} {anio}.
    </CardContent></Card>
  );
}

export function RepRolGeneral() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('');
  const { data, isFetching } = useQuery<RepRolGeneralData>({
    queryKey: ['nom-rep-rol-general', anio, mes, tipo],
    queryFn: () => nomRpc<RepRolGeneralData>('nom_rep_rol_general', { _anio: anio, _mes: mes, _tipo: tipo || null }),
  });

  // Exportar (CSV / XLS / PDF) — una barra por tabla de datos. Los KPI (tiles) no se exportan.
  const hoy = fechaLocal();
  const rubroRows = (data?.por_rubro ?? []).filter((r) => r.tipo !== 'provision');
  const provRows = (data?.por_rubro ?? []).filter((r) => r.tipo === 'provision');
  const totalRubros = rubroRows.reduce((a, r) => a + (r.tipo === 'egreso' ? -Number(r.total) : Number(r.total)), 0);
  const totalProv = provRows.reduce((a, r) => a + Number(r.total), 0);
  const deptoRows = data?.por_departamento ?? [];
  const headersRubroExp = ['N°', 'Rubro', 'Empleados', 'Total'];
  const filasRubroExp: Celda[][] = [
    ...rubroRows.map((r, i): Celda[] => [
      i + 1, `${r.descripcion} (${r.rubro_codigo})`, Number(r.n),
      r.tipo === 'egreso' ? -Number(r.total) : Number(r.total),
    ]),
    ...(rubroRows.length ? [['', 'TOTAL RUBROS (NETO)', '', totalRubros] as Celda[]] : []),
    ...(provRows.length ? [
      ['', 'PROVISIONES (costo patronal)', '', ''] as Celda[],
      ...provRows.map((r, i): Celda[] => [i + 1, `${r.descripcion} (${r.rubro_codigo})`, Number(r.n), Number(r.total)]),
      ['', 'TOTAL PROVISIONES', '', totalProv] as Celda[],
    ] : []),
  ];
  const headersDeptoExp = ['N°', 'Departamento', 'Empleados', 'Ingresos', 'Neto'];
  const filasDeptoExp: Celda[][] = [
    ...deptoRows.map((d, i): Celda[] => [i + 1, d.departamento, Number(d.n_empleados), Number(d.ingresos), Number(d.neto)]),
    ...(deptoRows.length
      ? [['', 'TOTAL',
          deptoRows.reduce((a, d) => a + Number(d.n_empleados ?? 0), 0),
          deptoRows.reduce((a, d) => a + Number(d.ingresos ?? 0), 0),
          deptoRows.reduce((a, d) => a + Number(d.neto ?? 0), 0)] as Celda[]]
      : []),
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Rol general consolidado</h1>
        <SelectorPeriodo anio={anio} mes={mes} setAnio={setAnio} setMes={setMes} cargando={isFetching} tipo={tipo} setTipo={setTipo} />
      </div>
      {!isFetching && !data?.rol && <SinRol anio={anio} mes={mes} />}
      {data?.rol && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['Empleados', String(data.rol.n_empleados)], ['Ingresos', money(data.rol.total_ingresos)],
              ['Egresos', money(data.rol.total_egresos)], ['Neto', money(data.rol.total_neto)]].map(([k, v]) => (
              <Card key={k}><CardContent className="py-3"><div className="text-xs text-muted-foreground">{k} ({data.rol!.estado})</div><div className="text-lg font-semibold font-mono tabular-nums">{v}</div></CardContent></Card>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Por rubro</CardTitle>
                <ExportarBotones
                  nombre={`rol-general-rubro-${hoy}`} titulo="Rol general consolidado — Por rubro"
                  headers={headersRubroExp} filas={filasRubroExp} colsDerecha={[0, 2, 3]}
                  subtitulo={`${MESES[mes - 1]} ${anio}`}
                  fmtPDF={(v, c) => (c === 3 ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
                  disabled={rubroRows.length === 0} />
              </CardHeader>
              <CardContent className="overflow-x-auto space-y-4">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Rubro</th><th className="py-2 pr-3 text-right">Empleados</th><th className="py-2 pr-3 text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {rubroRows.map((r) => (
                      <tr key={r.rubro_codigo} className="border-b last:border-0">
                        <td className="py-2 pr-3">{r.descripcion}<span className="text-xs text-muted-foreground font-mono ml-1">{r.rubro_codigo}</span></td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n}</td>
                        <td className={`py-2 pr-3 text-right font-mono tabular-nums font-semibold ${r.tipo === 'egreso' ? 'text-urgente' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {r.tipo === 'egreso' ? '−' : ''}{money(r.total)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3">Total rubros (neto)</td><td></td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-blue-600 dark:text-blue-400">{money(totalRubros)}</td>
                    </tr>
                  </tbody>
                </table>
                {provRows.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Provisiones <span className="font-normal normal-case">(costo patronal — no forma el neto)</span>
                    </p>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3">Provisión</th><th className="py-2 pr-3 text-right">Empleados</th><th className="py-2 pr-3 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {provRows.map((r) => (
                          <tr key={r.rubro_codigo} className="border-b last:border-0">
                            <td className="py-2 pr-3">{r.descripcion}<span className="text-xs text-muted-foreground font-mono ml-1">{r.rubro_codigo}</span></td>
                            <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n}</td>
                            <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-amber-600 dark:text-amber-400">{money(r.total)}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 font-semibold bg-muted/30">
                          <td className="py-2 pr-3">Total provisiones</td><td></td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums text-amber-600 dark:text-amber-400">{money(totalProv)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Por departamento</CardTitle>
                <ExportarBotones
                  nombre={`rol-general-departamento-${hoy}`} titulo="Rol general consolidado — Por departamento"
                  headers={headersDeptoExp} filas={filasDeptoExp} colsDerecha={[0, 2, 3, 4]}
                  subtitulo={`${MESES[mes - 1]} ${anio}`}
                  fmtPDF={(v, c) => ([3, 4].includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
                  disabled={deptoRows.length === 0} />
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Departamento</th><th className="py-2 pr-3 text-right">Empleados</th>
                    <th className="py-2 pr-3 text-right">Ingresos</th><th className="py-2 pr-3 text-right">Neto</th>
                  </tr></thead>
                  <tbody>
                    {data.por_departamento.map((d) => (
                      <tr key={d.departamento} className="border-b last:border-0">
                        <td className="py-2 pr-3">{d.departamento}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{d.n_empleados}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.ingresos)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(d.neto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

export function RepProvisiones() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('');
  const { data: rows = [], isFetching } = useQuery<RepProvisionRow[]>({
    queryKey: ['nom-rep-provisiones', anio, mes, tipo],
    queryFn: () => nomRpc<RepProvisionRow[]>('nom_rep_provisiones', { _anio: anio, _mes: mes, _tipo: tipo || null }),
  });
  const tot = (k: keyof RepProvisionRow) => rows.reduce((s, f) => s + Number(f[k] ?? 0), 0);

  // Exportar (CSV / XLS / PDF) — respeta período/tipo actuales; incluye fila de TOTAL de montos.
  const headersExp = ['N°', 'Empleado', 'Departamento', 'Base', 'D13', 'D14', 'Vacaciones', 'F. Reserva', 'Patronal', 'Total'];
  const MONEY_COLS = [3, 4, 5, 6, 7, 8, 9];
  const filasExport: Celda[][] = [
    ...rows.map((f, i): Celda[] => [
      i + 1, `${f.razon_social} (${f.identificacion})`, f.departamento ?? '',
      Number(f.base), Number(f.prov_d13), Number(f.prov_d14), Number(f.prov_vacaciones),
      Number(f.prov_fr), Number(f.ap_patronal), Number(f.total),
    ]),
    ...(rows.length
      ? [['', '', 'TOTAL', tot('base'), tot('prov_d13'), tot('prov_d14'), tot('prov_vacaciones'), tot('prov_fr'), tot('ap_patronal'), tot('total')] as Celda[]]
      : []),
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Provisiones del mes</h1>
        <SelectorPeriodo anio={anio} mes={mes} setAnio={setAnio} setMes={setMes} cargando={isFetching} tipo={tipo} setTipo={setTipo} />
      </div>
      <p className="text-xs text-muted-foreground">
        Provisión mensual de los beneficios ACUMULADOS (los mensualizados ya son gasto del rol): décimos, vacaciones (1/24, Art. 71),
        fondos de reserva con derecho, y aporte patronal IESS + IECE + SECAP.
      </p>
      {!isFetching && rows.length === 0 && <SinRol anio={anio} mes={mes} />}
      {rows.length > 0 && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Detalle ({rows.length}) — total del mes {money(tot('total'))}</CardTitle>
            <ExportarBotones
              nombre={`provisiones-${fechaLocal()}`} titulo="Provisiones del mes"
              headers={headersExp} filas={filasExport} colsDerecha={[0, ...MONEY_COLS]}
              subtitulo={`${MESES[mes - 1]} ${anio}`}
              fmtPDF={(v, c) => (MONEY_COLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
              disabled={rows.length === 0} />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Departamento</th>
                <th className="py-2 pr-3 text-right">Base</th><th className="py-2 pr-3 text-right">D13</th>
                <th className="py-2 pr-3 text-right">D14</th><th className="py-2 pr-3 text-right">Vacaciones</th>
                <th className="py-2 pr-3 text-right">F. Reserva</th><th className="py-2 pr-3 text-right">Patronal</th>
                <th className="py-2 pr-3 text-right">Total</th>
              </tr></thead>
              <tbody>
                {rows.map((f, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3">{f.razon_social}<div className="text-xs text-muted-foreground font-mono">{f.identificacion}</div></td>
                    <td className="py-2 pr-3 text-muted-foreground">{f.departamento}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.base)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.prov_d13)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.prov_d14)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.prov_vacaciones)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.prov_fr)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.ap_patronal)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(f.total)}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={2}>TOTALES</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('base'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('prov_d13'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('prov_d14'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('prov_vacaciones'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('prov_fr'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ap_patronal'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('total'))}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function RepCostoLaboral() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('');
  const { data: rows = [], isFetching } = useQuery<RepCostoRow[]>({
    queryKey: ['nom-rep-costo', anio, mes, tipo],
    queryFn: () => nomRpc<RepCostoRow[]>('nom_rep_costo_laboral', { _anio: anio, _mes: mes, _tipo: tipo || null }),
  });
  const tot = (k: keyof RepCostoRow) => rows.reduce((s, f) => s + Number(f[k] ?? 0), 0);

  // Exportar (CSV / XLS / PDF) — respeta período/tipo actuales; incluye fila de TOTAL de montos.
  const headersExp = ['N°', 'Departamento', 'Empleados', 'Remuneraciones', 'Provisiones', 'Aporte patronal', 'Costo total'];
  const MONEY_COLS = [3, 4, 5, 6];
  const filasExport: Celda[][] = [
    ...rows.map((f, i): Celda[] => [
      i + 1, f.departamento, Number(f.n_empleados),
      Number(f.remuneraciones), Number(f.provisiones), Number(f.ap_patronal), Number(f.costo_total),
    ]),
    ...(rows.length
      ? [['', 'TOTAL', tot('n_empleados'), tot('remuneraciones'), tot('provisiones'), tot('ap_patronal'), tot('costo_total')] as Celda[]]
      : []),
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Costo laboral por departamento</h1>
        <SelectorPeriodo anio={anio} mes={mes} setAnio={setAnio} setMes={setMes} cargando={isFetching} tipo={tipo} setTipo={setTipo} />
      </div>
      <p className="text-xs text-muted-foreground">
        Costo total para la empresa = remuneraciones pagadas en el rol + provisiones de beneficios + aporte patronal.
      </p>
      {!isFetching && rows.length === 0 && <SinRol anio={anio} mes={mes} />}
      {rows.length > 0 && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Departamentos ({rows.length}) — costo total {money(tot('costo_total'))}</CardTitle>
            <ExportarBotones
              nombre={`costo-laboral-${fechaLocal()}`} titulo="Costo laboral por departamento"
              headers={headersExp} filas={filasExport} colsDerecha={[0, 2, ...MONEY_COLS]}
              subtitulo={`${MESES[mes - 1]} ${anio}`}
              fmtPDF={(v, c) => (MONEY_COLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
              disabled={rows.length === 0} />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Departamento</th><th className="py-2 pr-3 text-right">Empleados</th>
                <th className="py-2 pr-3 text-right">Remuneraciones</th><th className="py-2 pr-3 text-right">Provisiones</th>
                <th className="py-2 pr-3 text-right">Aporte patronal</th><th className="py-2 pr-3 text-right">Costo total</th>
              </tr></thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.departamento} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3">{f.departamento}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{f.n_empleados}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.remuneraciones)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.provisiones)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.ap_patronal)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(f.costo_total)}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-muted/30">
                  <td className="py-2 pr-3">TOTAL EMPRESA</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{tot('n_empleados')}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('remuneraciones'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('provisiones'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ap_patronal'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('costo_total'))}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
